import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Badge, Modal, Spinner, Button } from 'react-bootstrap';
import BootstrapCard from 'react-bootstrap/Card';
import { useSelector } from 'react-redux';
import Card from '../../../components/Card';
import { apiHandler } from '../../../api/apihandler';
import { TEACHERS_CLASS_STUDENTS_API, TEACHERS_ALLERGY_VIEW_API } from '../../../api/constants';
import { isTeacherLoginProfile } from '../../../utilities/teacherModulePermissions';

const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Teacher-facing "Allergy" left-menu page: every student across every class
// this teacher teaches, click a student to load and view their full Allergy
// Action Plan (a separate detail call, since the class-students list only
// carries whether a plan exists via allergy_report_id).
const TeacherAllergy = () => {
  // This page shows every student's allergy/EpiPen info across every class
  // a teacher teaches - it was reachable by direct URL from ANY signed-in
  // account (student/parent included), not just teachers, since nothing
  // here ever checked the role. Hiding the left-menu item (see
  // teacherModulePermissions.js's MODULE_ROUTE_PREFIXES) only stops
  // discovery via the UI, not direct navigation - so this needs its own
  // guard, same signal layouts/dashboard/default.js already uses to gate
  // teacher module routes.
  let profileData = useSelector((state) => state.setting.setting.user_data);
  profileData = profileData ? JSON.parse(profileData) : null;
  const isTeacher = isTeacherLoginProfile(profileData);

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!isTeacher) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiHandler(TEACHERS_CLASS_STUDENTS_API, 'POST', {});
        if (cancelled) return;
        // apiHandler() never throws - a failed request resolves with the raw
        // axios error instead, so without this check a 404/network failure
        // silently looked identical to "no students".
        if (res?.isAxiosError || (res?.response && res?.data === undefined)) {
          setError('Failed to load students.');
          return;
        }
        const list = res?.data?.data?.students;
        setStudents(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) setError('Failed to load students.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredStudents = students.filter((s) =>
    !search.trim() || s.name.toLowerCase().includes(search.trim().toLowerCase())
  );

  const handleViewDetails = async (student) => {
    setShowModal(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await apiHandler(TEACHERS_ALLERGY_VIEW_API, 'POST', { student_id: student.id });
      setDetail(res?.data?.data || null);
    } catch (err) {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setDetail(null);
  };

  const report = detail?.allergy_report;
  const allergyItems = report ? [
    report.is_peanuts === 1 && 'Peanuts',
    report.is_shellfish === 1 && `Shellfish${report.shellfish_specify ? ` (${report.shellfish_specify})` : ''}`,
    report.is_tree_nuts === 1 && 'Tree Nuts',
    report.is_fish === 1 && `Fish${report.fish_specify ? ` (${report.fish_specify})` : ''}`,
    report.is_milk === 1 && 'Milk/Dairy',
    report.is_food_additives === 1 && `Food Additives${report.food_additives_specify ? ` (${report.food_additives_specify})` : ''}`,
    report.all_dairy_products === 1 && 'All Dairy Products',
    report.insect_stings_bites === 1 && `Insect Stings/Bites${report.insect_stings_bites_specify ? ` (${report.insect_stings_bites_specify})` : ''}`,
    report.is_other === 1 && `Other${report.other_specify ? ` (${report.other_specify})` : ''}`,
  ].filter(Boolean) : [];

  if (!isTeacher) {
    return (
      <div id="content-page" className="content-page">
        <Container>
          <Row>
            <Col sm="12">
              <Card>
                <Card.Body className="text-center py-5">
                  <i className="material-symbols-outlined" style={{ fontSize: '48px', color: '#dc3545' }}>block</i>
                  <h5 className="mt-3 mb-1">Access Denied</h5>
                  <p className="text-muted mb-0">This page is only available to teacher accounts.</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div id="content-page" className="content-page">
      <style>{`
        .header-search-row {
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: start;
          gap: 12px;
          width: 100%;
        }
        @media (max-width: 767.98px) {
          .header-search-row {
            grid-template-columns: 1fr;
          }
          .header-search-row .student-search-wrap {
            width: 100%;
          }
        }
        .student-search-wrap { position: relative; }
        .student-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 18px;
          color: #9ca3af;
          pointer-events: none;
        }
        .student-search-input {
          padding-left: 40px;
          padding-right: 34px;
          height: 42px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          background-color: #f9fafb;
          font-size: 14px;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
        }
        .student-search-input:focus {
          background-color: #fff;
          border-color: #6f42c1;
          box-shadow: 0 0 0 3px rgba(111, 66, 193, 0.12);
        }
        .student-search-clear {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          width: 26px;
          height: 26px;
          background: none;
          border: none;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          cursor: pointer;
          border-radius: 50%;
        }
        .student-search-clear:hover {
          background-color: #f3f4f6;
          color: #374151;
        }
        .student-search-clear i {
          font-size: 16px;
        }
      `}</style>
      <Container>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Header>
                <div className="header-search-row">
                  {/* Grid (1fr auto) instead of flex - guarantees the title
                      takes the remaining space and the search box sits
                      flush against the row's right edge, with no flex
                      grow/shrink/wrap ambiguity between the two. */}
                  <div className="header-title mb-2 mb-md-0" style={{ minWidth: 0 }}>
                    <h4 className="card-title">
                      <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>medical_information</i>
                      Allergy Action Plans
                    </h4>
                    <p className="text-muted mb-0">Allergy Action Plans for every student in your class</p>
                  </div>
                  <div className="student-search-wrap" style={{ minWidth: '260px' }}>
                    <i className="material-symbols-outlined student-search-icon">search</i>
                    <input
                      type="text"
                      className="form-control student-search-input"
                      placeholder="Search by student name..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                      <button
                        type="button"
                        className="student-search-clear"
                        aria-label="Clear search"
                        onClick={() => setSearch('')}
                      >
                        <i className="material-symbols-outlined">close</i>
                      </button>
                    )}
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" role="status" style={{ color: '#6f42c1' }}>
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3 text-muted mb-0">Loading students...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-5">
                    <i className="material-symbols-outlined" style={{ fontSize: '48px', color: '#dc3545' }}>error</i>
                    <p className="text-danger mt-2 mb-0">{error}</p>
                  </div>
                ) : filteredStudents.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="material-symbols-outlined" style={{ fontSize: '64px', color: '#ccc' }}>group_off</i>
                    <p className="mt-3 text-muted mb-0">
                      {students.length === 0 ? 'No students are currently assigned to your class(es).' : 'No students match your search.'}
                    </p>
                  </div>
                ) : (
                  <Row className="g-3">
                    {filteredStudents.map((student) => {
                      const hasPlan = !!student.allergy_report_id;
                      return (
                        <Col xs="12" md="6" lg="4" key={student.id}>
                          <BootstrapCard
                            className="h-100 shadow-sm"
                            role="button"
                            onClick={() => handleViewDetails(student)}
                            style={{ cursor: 'pointer', border: '1px solid #eef0f3', borderRadius: '12px' }}
                          >
                            <BootstrapCard.Body className="d-flex align-items-center gap-3">
                              {student.photo ? (
                                <img
                                  src={student.photo}
                                  alt={student.name}
                                  style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }}
                                />
                              ) : (
                                <div
                                  className="d-flex align-items-center justify-content-center text-white flex-shrink-0"
                                  style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#6f42c1', fontSize: '16px', fontWeight: 600 }}
                                >
                                  {student.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                              )}
                              <div className="flex-grow-1">
                                <div className="fw-bold text-dark">{student.name}</div>
                                {student.class_name && (
                                  <div className="text-muted small">{student.class_name}</div>
                                )}
                              </div>
                              <Badge
                                bg={hasPlan ? 'warning' : 'light'}
                                text={hasPlan ? 'dark' : undefined}
                                className="rounded-pill"
                                style={!hasPlan ? { color: '#8a8f98', border: '1px solid #dcdfe3' } : {}}
                              >
                                {hasPlan ? 'Has plan' : 'No plan'}
                              </Badge>
                            </BootstrapCard.Body>
                          </BootstrapCard>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle', color: '#f59e0b' }}>warning</i>
            {detail?.student?.name ? `${detail.student.name}'s Allergy Action Plan` : 'Allergy Action Plan'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {detailLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" style={{ color: '#6f42c1' }}>
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : !report ? (
            <div className="text-center py-4">
              <i className="material-symbols-outlined" style={{ fontSize: '48px', color: '#ccc' }}>medical_information</i>
              <p className="text-muted mt-2 mb-0">No Allergy Action Plan on file for this student.</p>
            </div>
          ) : (
            <>
              <Row className="g-3 mb-3">
                <Col md="6">
                  <div className="p-3 h-100" style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '10px' }}>
                    <div className="fw-bold mb-2" style={{ color: '#92400e', fontSize: '13px' }}>⚠️ Allergies</div>
                    {allergyItems.length === 0 ? (
                      <div className="text-muted small">None recorded</div>
                    ) : (
                      <ul className="mb-0 ps-3" style={{ fontSize: '13px' }}>
                        {allergyItems.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    )}
                  </div>
                </Col>
                <Col md="6">
                  <div className="p-3 h-100" style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '10px' }}>
                    <div className="fw-bold mb-2" style={{ color: '#991b1b', fontSize: '13px' }}>🏥 Emergency Treatment</div>
                    {report.emergency_treatment_procedures && (
                      <div className="mb-2">
                        <div className="fw-semibold" style={{ fontSize: '11px', color: '#991b1b' }}>Procedures</div>
                        <div style={{ fontSize: '13px' }}>{report.emergency_treatment_procedures}</div>
                      </div>
                    )}
                    {report.require_epi_pen === 1 && (
                      <div className="mb-2 p-2" style={{ background: '#fecaca', borderRadius: '6px' }}>
                        <div className="fw-semibold" style={{ fontSize: '11px', color: '#991b1b' }}>💉 EpiPen Required</div>
                        {report.location_of_epi_pen && (
                          <div style={{ fontSize: '12px', color: '#991b1b' }}>Location: {report.location_of_epi_pen}</div>
                        )}
                      </div>
                    )}
                    {report.symptoms && (
                      <div>
                        <div className="fw-semibold" style={{ fontSize: '11px', color: '#991b1b' }}>Symptoms</div>
                        <div style={{ fontSize: '13px' }}>{report.symptoms}</div>
                      </div>
                    )}
                    {!report.emergency_treatment_procedures && report.require_epi_pen !== 1 && !report.symptoms && (
                      <div className="text-muted small">None recorded</div>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="g-3">
                <Col md="6">
                  <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>Parent Signature</div>
                  {report.parent_signature ? (
                    <div className="p-2" style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                      <img src={report.parent_signature} alt="Parent Signature" style={{ maxWidth: '100%', maxHeight: '60px', display: 'block' }} />
                      {formatDate(report.parent_signature_date) && (
                        <div className="text-muted small mt-1">Signed: {formatDate(report.parent_signature_date)}</div>
                      )}
                    </div>
                  ) : (
                    <div className="text-muted small fst-italic">Not signed</div>
                  )}
                </Col>
                <Col md="6">
                  <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>Supervisor Signature</div>
                  {report.supervisor_signature ? (
                    <div className="p-2" style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                      <img src={report.supervisor_signature} alt="Supervisor Signature" style={{ maxWidth: '100%', maxHeight: '60px', display: 'block' }} />
                      {formatDate(report.supervisor_signature_date) && (
                        <div className="text-muted small mt-1">Signed: {formatDate(report.supervisor_signature_date)}</div>
                      )}
                    </div>
                  ) : (
                    <div className="text-muted small fst-italic">Not signed</div>
                  )}
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TeacherAllergy;
